import { SearchResults, TrendingItem, Product, Suggestion } from "../../domain/models/SearchSuggestion";
import type { SearchSuggestionsResponseDTO } from "../dto/searchSuggestion.dto";

/**
 * Adapter converts DTO/Plain Object -> Domain model and validates minimal invariants.
 * This is required when the dto is not the same as the domain model
 * Applies domain logic (defaults, transformations)
 * Works with both DTO (from API) and plain object (from Redux)
 */
export function adaptSearchResponseDtoToDomain(
  input: SearchSuggestionsResponseDTO | Record<string, unknown>
): SearchResults {
  // Normalize input to handle both DTO and plain object
  const trending = input.trending as { list: Array<{ name: string; url: string }>; trendingTitle?: string } | undefined;
  const suggestions = input.suggestions as (string | { title: string })[] | undefined;
  const products = input.products as {
    productTitle?: string;
    list: Array<{ image: string; name: string; url: string; designId?: string | number }>;
  } | undefined;
  // Handle both DTO (search_text) and plain object (searchText) property names
  const searchText = ('search_text' in input 
    ? (input as SearchSuggestionsResponseDTO).search_text 
    : (input as Record<string, unknown>).searchText) as string | undefined;

  // Handle trending (only present when search_text is empty)
  const trendingList = (trending?.list || []).map((item) =>
    new TrendingItem({
      name: item.name,
      url: item.url,
    })
  );

  // Handle suggestions - can be string[] or SuggestionItemDTO[]
  const suggestionsList: (string | Suggestion)[] = [];
  if (suggestions) {
    suggestions.forEach((item) => {
      if (typeof item === 'string') {
        suggestionsList.push(item);
      } else if (item && typeof item === 'object' && 'title' in item) {
        suggestionsList.push(new Suggestion({ title: item.title }));
      }
    });
  }

  // Handle products
  const productList = (products?.list || []).map((item) => {
    const designIdRaw = item.designId;
    const designId =
      designIdRaw != null && String(designIdRaw).trim() !== ''
        ? String(designIdRaw).trim()
        : undefined;
    return new Product({
      image: item.image,
      name: item.name,
      url: item.url,
      designId,
    });
  });

  return new SearchResults({
    trending: trending ? {
      list: trendingList,
      trendingTitle: trending.trendingTitle || "Trending",
    } : undefined,
    suggestions: suggestionsList,
    products: {
      productTitle: products?.productTitle || (searchText ? `Search Results for '${searchText}'` : "What's new"),
      list: productList,
    },
    searchText,
  });
}

/**
 * Convert domain model (class instances) to plain object for Redux serialization
 */
export function searchResultsToPlainObject(results: SearchResults): Record<string, unknown> {
  return {
    trending: results.trending ? {
      list: results.trending.list.map(item => ({
        name: item.name,
        url: item.url,
      })),
      trendingTitle: results.trending.trendingTitle,
    } : undefined,
    suggestions: results.suggestions.map(item => 
      typeof item === 'string' ? item : { title: item.title }
    ),
    products: {
      productTitle: results.products.productTitle,
      list: results.products.list.map(item => ({
        image: item.image,
        name: item.name,
        url: item.url,
        ...(item.designId ? { designId: item.designId } : {}),
      })),
    },
    searchText: results.searchText,
  };
}

